import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone,
  Mail,
  Star,
  Award,
  Store
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';

const VendorDetailPage = () => {
  const { vendor_id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({
    reviewer_name: '',
    reviewer_email: '',
    rating: 5,
    comment: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchVendor();
    fetchReviews();
  }, [vendor_id]);

  const fetchVendor = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      // We need to get vendor by ID - let's fetch all and find by ID
      const response = await fetch(`${backendUrl}/api/vendors`);
      
      if (response.ok) {
        const vendors = await response.json();
        const vendorData = vendors.find(v => v.vendor_id === vendor_id);
        if (vendorData) {
          setVendor(vendorData);
        } else {
          toast.error('Vendor not found');
          navigate('/');
        }
      } else {
        toast.error('Failed to load vendor');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching vendor:', error);
      toast.error('Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/vendors/${vendor_id}/reviews`);
      
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/vendors/${vendor_id}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData)
      });
      
      if (response.ok) {
        toast.success('Review submitted successfully!');
        setReviewData({ reviewer_name: '', reviewer_email: '', rating: 5, comment: '' });
        setShowReviewForm(false);
        fetchReviews();
        fetchVendor(); // Refresh to get updated rating
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-accent border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading vendor...</p>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-muted-foreground">Vendor not found</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Navigation showBackButton={true} backTo="/" />

      {/* Vendor Content */}
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Vendor Header */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  {vendor.image ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${vendor.image}`}
                      alt={vendor.vendor_name}
                      className="w-48 h-48 rounded-lg object-cover border-2 border-border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`w-48 h-48 rounded-lg bg-gradient-to-br from-accent to-accent/70 ${vendor.image ? 'hidden' : 'flex'} items-center justify-center text-6xl font-bold text-white`}>
                    {vendor.vendor_name.substring(0, 2).toUpperCase()}
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h1 className="text-3xl font-bold mb-2">{vendor.vendor_name}</h1>
                      <p className="text-xl text-accent font-medium">{vendor.category}</p>
                    </div>
                    {vendor.verified && (
                      <Badge className="bg-accent/10 text-accent border-accent/20">
                        <Award className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Store className="w-4 h-4" />
                      <span>{vendor.department.replace(/-/g, ' ').toUpperCase()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{vendor.city}, {vendor.state}</span>
                    </div>
                    {vendor.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span>{vendor.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-3 mt-4">
                    {vendor.mobile_no && (
                      <Button variant="outline" size="sm">
                        <Phone className="w-4 h-4 mr-2" />
                        {vendor.mobile_no}
                      </Button>
                    )}
                    {vendor.address && (
                      <Button variant="outline" size="sm">
                        <Mail className="w-4 h-4 mr-2" />
                        Contact
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          {vendor.description && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-3">About</h2>
                <p className="text-muted-foreground">{vendor.description}</p>
              </CardContent>
            </Card>
          )}

          {/* Services */}
          {vendor.services && vendor.services.length > 0 && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <Store className="w-5 h-5 text-accent" />
                  Services Offered
                </h2>
                <div className="flex flex-wrap gap-2">
                  {vendor.services.map((service, idx) => (
                    <Badge key={idx} className="bg-accent/10 text-accent border-accent/20">
                      {service}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Address */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-accent" />
                Location
              </h2>
              <p className="text-muted-foreground">{vendor.address}</p>
              {vendor.location_pin && (
                <p className="text-sm text-muted-foreground mt-2">
                  PIN: {vendor.location_pin}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Reviews Section */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Star className="w-5 h-5 text-accent" />
                  Reviews & Ratings
                </h2>
                <Button 
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  className="bg-accent hover:bg-accent/90"
                >
                  {showReviewForm ? 'Cancel' : 'Write a Review'}
                </Button>
              </div>

              {/* Review Stats */}
              {vendor.review_count > 0 && (
                <div className="mb-6 p-4 bg-accent/5 rounded-lg border border-accent/10">
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-accent">{vendor.rating.toFixed(1)}</div>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= vendor.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {vendor.review_count} {vendor.review_count === 1 ? 'review' : 'reviews'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Review Form */}
              {showReviewForm && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  onSubmit={handleSubmitReview}
                  className="mb-8 p-6 bg-accent/5 rounded-lg border border-accent/10 space-y-4"
                >
                  <h3 className="font-semibold text-lg mb-4">Share Your Experience</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Your Name *</label>
                      <input
                        type="text"
                        required
                        value={reviewData.reviewer_name}
                        onChange={(e) => setReviewData({ ...reviewData, reviewer_name: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-border bg-background"
                        placeholder="Enter your name"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-2 block">Your Email</label>
                      <input
                        type="email"
                        value={reviewData.reviewer_email}
                        onChange={(e) => setReviewData({ ...reviewData, reviewer_email: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-border bg-background"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Rating *</label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewData({ ...reviewData, rating: star })}
                          className="transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-8 h-8 ${
                              star <= reviewData.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-muted-foreground">
                        {reviewData.rating} {reviewData.rating === 1 ? 'star' : 'stars'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Your Review *</label>
                    <textarea
                      required
                      value={reviewData.comment}
                      onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-border bg-background min-h-[120px]"
                      placeholder="Share your experience with this vendor..."
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowReviewForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submittingReview}
                      className="flex-1 bg-accent hover:bg-accent/90"
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                </motion.form>
              )}

              {/* Reviews List */}
              <div className="space-y-4">
                {loadingReviews ? (
                  <p className="text-center text-muted-foreground py-8">Loading reviews...</p>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
                  </div>
                ) : (
                  reviews.map((review) => (
                    <div
                      key={review.review_id}
                      className="p-4 rounded-lg border border-border bg-background/50"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold">{review.reviewer_name}</p>
                          <div className="flex items-center gap-1 mt-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= review.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-muted-foreground mt-2">{review.comment}</p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default VendorDetailPage;
